import { useCallback, useEffect, useRef, useState } from "react";
import { EonUiPreloaderSquares } from "@eon-ui/eon-ui-components-react";
import { useJwtToken } from "@me8eon/react_authentication";
import type {
    ItemDetail,
    PolicyHygieneRow,
    TriageActionState,
    TriageSuccess,
} from "../aidwops.types";
import useAidwopsTriageStore, { NO_SELECTION } from "../AidwopsTriageStore";
import { api } from "../expertApi";
import { ChoiceButton, TriageNotice, TriageToolbar } from "./TriageParts";
import "./ExpertQueue.scss";

type LoadState =
    | { status: "loading" }
    | { status: "error"; message: string }
    | { status: "ready"; rows: PolicyHygieneRow[] };

/**
 * A placeholder for a future "stage in ServiceNow" action, mirroring `BatteryDevicesSection`'s real one. The
 * policy clean-up SNOW flow has no endpoint yet, so this only simulates the wait and never calls any API.
 */
type DummySnowState =
    | { status: "idle" }
    | { status: "creating" }
    | { status: "created"; count: number; ignored: number };

const STATUS_LABEL: Record<PolicyHygieneRow["status"], string> = {
    unassigned: "unassigned",
    superseded: "superseded",
    duplicate: "duplicate",
    redundant: "redundant",
};

const keyOf = (row: PolicyHygieneRow, index: number) =>
    row.policy_id ?? `${row.status}-${row.policy_name ?? "unnamed"}-${index}`;

/**
 * The full, named list of Intune policy hygiene findings (unassigned, superseded, duplicate, redundant), read
 * live from `GET /api/intune/policy-hygiene`. Unlike the queue finding's own evidence text -- which only
 * carries per-category counts and a handful of sample names -- this endpoint reshapes the Intune MCP tool's
 * output into one row per policy, so every finding can be listed and triaged, not just the sampled few.
 */
export function PolicyHygieneSection({
    item,
    onActionChange,
    onSuccess,
}: {
    item: ItemDetail;
    onActionChange: (action: TriageActionState | null) => void;
    onSuccess: (result: TriageSuccess) => void;
}) {
    const jwtToken = useJwtToken();
    const selection =
        useAidwopsTriageStore((state) => state.selections[item.id]) ??
        NO_SELECTION;
    const toggle = useAidwopsTriageStore((state) => state.toggle);
    const setMany = useAidwopsTriageStore((state) => state.setMany);
    const [state, setState] = useState<LoadState>({
        status: "loading",
    });
    const [attempt, setAttempt] = useState(0);
    const [snow, setSnow] = useState<DummySnowState>({
        status: "idle",
    });

    const [stagedKeys, setStagedKeys] = useState<Set<string>>(() => new Set());

    /*
     * Derive safe values for every render.
     * When loading or failed, rawRows is simply empty.
     */
    const rawRows = state.status === "ready" ? state.rows : [];

    const visible = rawRows
        .map((row, index) => ({
            row,
            key: keyOf(row, index),
        }))
        .filter(({ key }) => !stagedKeys.has(key));

    const rows = visible.map(({ row }) => row);
    const keys = visible.map(({ key }) => key);

    const cleanupKeys = keys.filter((key) => selection[key] === "cleanup");

    const cleanup = cleanupKeys.length;

    const ignored = keys.filter((key) => selection[key] === "ignore").length;

    /*
     * Store the latest action values so the function exposed to the parent
     * always uses the current selection without being recreated constantly.
     */
    const actionDataRef = useRef({
        cleanup: 0,
        ignored: 0,
        cleanupKeys: [] as string[],
    });

    const onSuccessRef = useRef(onSuccess);

    actionDataRef.current = {
        cleanup,
        ignored,
        cleanupKeys,
    };

    onSuccessRef.current = onSuccess;

    /*
     * All hooks are declared before conditional returns.
     */
    useEffect(() => {
        setSnow((current) =>
            current.status === "creating" ? current : { status: "idle" }
        );
    }, [selection]);

    useEffect(() => {
        let isCurrent = true;
        setState({ status: "loading" });

        void (async () => {
            try {
                const accessToken = await jwtToken();
                if (!accessToken) {
                    throw new Error(
                        "Unable to acquire access token for expert API"
                    );
                }
                const hygiene = await api.getPolicyHygiene(accessToken);
                if (isCurrent) {
                    setState({
                        status: "ready",
                        rows: hygiene.rows,
                    });
                }
            } catch (error) {
                if (isCurrent) {
                    setState({
                        status: "error",
                        message:
                            error instanceof Error
                                ? error.message
                                : String(error),
                    });
                }
            }
        })();

        return () => {
            isCurrent = false;
        };
        // jwtToken follows useJwtToken's unstable function identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt]);

    const stageSnowCleanup = useCallback(async (): Promise<void> => {
        const {
            cleanup: currentCleanup,
            ignored: currentIgnored,
            cleanupKeys: currentCleanupKeys,
        } = actionDataRef.current;

        if (currentCleanup === 0) {
            return;
        }

        setSnow((current) => {
            if (current.status === "creating") {
                return current;
            }

            return { status: "creating" };
        });

        await new Promise<void>((resolve) => {
            window.setTimeout(resolve, 300);
        });

        setSnow({
            status: "created",
            count: currentCleanup,
            ignored: currentIgnored,
        });

        setStagedKeys(
            (current) => new Set([...current, ...currentCleanupKeys])
        );

        onSuccessRef.current({
            title: "Policy clean-up staged",
            message: `Clean-up was staged for ${currentCleanup} ${
                currentCleanup === 1 ? "policy" : "policies"
            }. ${currentIgnored} ignored.`,
            tickets: [],
        });
    }, []);

    useEffect(() => {
        onActionChange({
            label: "Stage Clean-up",
            count: cleanup,
            isSubmitting: snow.status === "creating",
            disabled:
                state.status !== "ready" ||
                cleanup === 0 ||
                snow.status === "creating" ||
                snow.status === "created",
            execute: stageSnowCleanup,
        });
    }, [cleanup, onActionChange, snow.status, stageSnowCleanup, state.status]);

    useEffect(() => {
        return () => {
            onActionChange(null);
        };
    }, [onActionChange]);

    const label = (
        <div className="expert-queue-item-detail-card-section-label">
            Policy clean-up triage
        </div>
    );

    /*
     * Conditional returns come only after every hook.
     */
    if (state.status === "loading") {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <div className="triage-loading">
                    <EonUiPreloaderSquares />
                    <p className="expert-queue-item-detail-card-section-text">
                        Loading the policies...
                    </p>
                </div>
            </div>
        );
    }

    if (state.status === "error") {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <div className="ac-alert" role="alert">
                    <p className="ac-alert-title">
                        Couldn't load the policies.
                    </p>
                    <p className="ac-alert-detail">{state.message}</p>
                </div>
                <div>
                    <button
                        type="button"
                        className="triage-button"
                        onClick={() => setAttempt((count) => count + 1)}
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (rawRows.length === 0) {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <p className="expert-queue-item-detail-card-section-text">
                    No unassigned, superseded, duplicate or redundant policies
                    were found.
                </p>
            </div>
        );
    }

    return (
        <div className="expert-queue-item-detail-card-section">
            {label}
            <p className="expert-queue-item-detail-card-section-text">
                {rows.length} {rows.length === 1 ? "policy" : "policies"} named
                below, read live from the Intune policy hygiene index.
            </p>

            <TriageToolbar
                counts={[
                    {
                        label: "clean up",
                        value: cleanup,
                    },
                    {
                        label: "ignored",
                        value: ignored,
                    },
                    {
                        label: "not reviewed",
                        value: keys.length - cleanup - ignored,
                    },
                ]}
                actions={[
                    {
                        label: "Clean up all",
                        onClick: () => setMany(item.id, keys, "cleanup"),
                    },
                    {
                        label: "Ignore all",
                        onClick: () => setMany(item.id, keys, "ignore"),
                    },
                    {
                        label: "Reset",
                        onClick: () => setMany(item.id, keys, null),
                    },
                ]}
            />

            {rows.length > 0 ? (
                <ul className="triage-list">
                    {rows.map((row, index) => {
                        const key = keys[index];
                        const choice = selection[key];

                        const name =
                            row.policy_name ??
                            row.policy_id ??
                            "Unknown policy";

                        return (
                            <li
                                key={key}
                                className="triage-row"
                                data-choice={choice ?? ""}
                            >
                                <div className="triage-row-top">
                                    <div className="triage-name">
                                        <strong>{name}</strong>

                                        {row.policy_type && (
                                            <span className="triage-sub">
                                                {row.policy_type}
                                            </span>
                                        )}
                                    </div>

                                    <span
                                        className={`expert-queue-view-tag expert-queue-view-tag--${
                                            row.status === "unassigned"
                                                ? "high"
                                                : "medium"
                                        }`}
                                    >
                                        {STATUS_LABEL[row.status]}
                                    </span>
                                </div>

                                {(row.recommendation || row.related_policy) && (
                                    <p className="expert-queue-item-detail-card-section-text">
                                        {row.recommendation}

                                        {row.related_policy
                                            ? `${
                                                  row.recommendation ? " " : ""
                                              }Related policy: ${
                                                  row.related_policy
                                              }.`
                                            : ""}
                                    </p>
                                )}

                                <div className="triage-actions">
                                    <ChoiceButton
                                        label="Clean up"
                                        tone="act"
                                        pressed={choice === "cleanup"}
                                        onClick={() =>
                                            toggle(item.id, key, "cleanup")
                                        }
                                    />

                                    <ChoiceButton
                                        label="Ignore"
                                        tone="ignore"
                                        pressed={choice === "ignore"}
                                        onClick={() =>
                                            toggle(item.id, key, "ignore")
                                        }
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className="expert-queue-item-detail-card-section-text">
                    All flagged policies have been staged for clean-up.
                </p>
            )}

            <TriageNotice what="A clean-up marks the policy for retirement by its owners." />
        </div>
    );
}

export default PolicyHygieneSection;
